import { native } from '@forest/native-services'
import { Dialog } from '@kobalte/core'
import { Fit, Layout, Rive, RiveFile } from '@rive-app/canvas-lite'
import { createSignal, onCleanup, onMount } from 'solid-js'
import { Motion } from 'solid-motionone'
import anim from '~/assets/anim/subscribed.riv?url'
import bgImg from '~/assets/images/bg.svg'
import { platform } from '~/lib/params'
import { m } from '~/paraglide/messages'

export function SubscribedView(props: {
  isOpen: boolean
  isTrial?: boolean
  onAnimLoaded: () => void
}) {
  const riveFile = new RiveFile({ src: anim })

  const loadingRiveFile = (async () => {
    await riveFile.init()
    return riveFile
  })()

  const [isBtnRevealed, setIsBtnRevealed] = createSignal(false)

  loadingRiveFile.finally(() => {
    props.onAnimLoaded()
  })

  const handleCompleteBtnClick = () => {
    native.general.close()
  }

  const titleMessage = () => {
    if (platform === 'china_android') {
      return props.isTrial
        ? m.onboarding_paywall_subscribed_success_title_trial_cna()
        : m.onboarding_paywall_subscribed_success_title_cna()
    }
    return m.onboarding_paywall_subscribed_success_title()
  }

  const contentMessage = () => {
    if (platform === 'china_android') {
      return m.onboarding_paywall_subscribed_success_content_trial_cna()
    }
    return m.onboarding_paywall_subscribed_success_content()
  }

  return (
    <Dialog.Root open={props.isOpen}>
      <Dialog.Content
        class="fixed inset-0 z-50"
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <Motion.div
          class="size-full bg-[#001721] px-8 pt-[calc(60px+env(safe-area-inset-top))] text-center text-[#F3FEFF]"
          style={{
            'background-image': `url(${bgImg})`,
            'background-repeat': 'no-repeat',
            'background-size': 'cover',
            'background-position': 'center',
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <div class="h-[30vh] w-full">
            <Anim loadingRiveFile={loadingRiveFile} />
          </div>
          <Motion.div
            class="mt-6 text-[36px] font-bold"
            style={{
              'text-shadow': '0 0 10px rgba(255, 255, 255, 0.5)',
            }}
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              delay: 3,
              opacity: {
                easing: [0.33, 1, 0.68, 1],
                duration: 0.6,
              },
              y: {
                easing: [0.5, 1, 0.89, 1],
                duration: 0.6,
              },
            }}
          >
            {titleMessage()}
          </Motion.div>
          <Motion.div
            class="mt-2 flex h-[112px] items-center justify-center text-[20px] font-bold"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              delay: 3.5,
              opacity: {
                easing: [0.33, 1, 0.68, 1],
                duration: 0.6,
              },
              y: {
                easing: [0.5, 1, 0.89, 1],
                duration: 0.6,
              },
            }}
          >
            {contentMessage()}
          </Motion.div>
        </Motion.div>

        <Motion.div
          class="absolute bottom-12 left-0 right-0 px-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{
            delay: 4.5,
            easing: [0.33, 1, 0.68, 1],
            duration: 0.3,
          }}
          onMotionComplete={() => {
            setIsBtnRevealed(true)
          }}
        >
          <button
            class="group h-[52px] w-full rounded-xl p-px [background-image:linear-gradient(180deg,#C5FF70_0%,#69FF78_45%,#30D3FF_100%)]"
            disabled={!isBtnRevealed()}
            onClick={handleCompleteBtnClick}
          >
            <span class="flex h-full w-full items-center justify-center rounded-[11px] font-bold text-[#176361] [background-image:linear-gradient(106deg,#C5FF70_13%,#30D3FF_97%)] group-active:[background-image:linear-gradient(90deg,#94D66E_0%,#2BB8D3_100%)]">
              {m.onboarding_paywall_subscribed_success_btn()}
            </span>
          </button>
        </Motion.div>
      </Dialog.Content>
    </Dialog.Root>
  )
}

export function Anim(props: { loadingRiveFile: Promise<RiveFile> }) {
  let canvas!: HTMLCanvasElement // oxlint-disable-line eslint/no-unassigned-vars
  let rive: Rive | null
  const handleResize = () => {
    rive?.resizeDrawingSurfaceToCanvas()
  }
  onMount(() => {
    props.loadingRiveFile.then((riveFile) => {
      rive = new Rive({
        riveFile,
        artboard: 'paywall_success',
        stateMachines: ['State Machine 1'],
        canvas,
        autoplay: true,
        layout: new Layout({ fit: Fit.FitHeight }),
        onLoad: () => {
          rive?.resizeDrawingSurfaceToCanvas()
          window.addEventListener('resize', handleResize)
        },
      })
    })
  })
  onCleanup(() => {
    rive?.stopRendering()
    rive?.cleanupInstances()
    rive = null
    window.removeEventListener('resize', handleResize)
  })
  return <canvas ref={canvas} class="size-full" />
}
